import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SectionMarker from '@/components/sections/SectionMarker';
import PageHero from '@/components/sections/PageHero';
import { PHOTOS } from '@/constants/content';
import { digitalApi } from '@/lib/digitalApi';
import { formatMonthYear } from '@/constants/resourceSections';
import { ProductCard } from '@/components/digital/ProductCard';
import { JOURNAL as J_TID } from '@/constants/testIds';
import { useReveal } from '@/lib/useReveal';
import { usePageMeta } from '@/lib/usePageMeta';
import { SEO } from '@/constants/seo';

export default function Journal() {
  usePageMeta({ ...SEO.journal });
  const [entries, setEntries] = useState(null);
  const [downloads, setDownloads] = useState([]);
  useEffect(() => {
    digitalApi.list({ section: 'journal', content_type: 'article' }).then((d) => setEntries(d.items)).catch(() => setEntries([]));
    digitalApi.list({ section: 'journal', content_type: 'download' }).then((d) => setDownloads(d.items)).catch(() => setDownloads([]));
  }, []);
  return (
    <div className="bg-ivory">
      <section className="pt-40 lg:pt-56 pb-16 lg:pb-24">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16 items-end">
          <div className="lg:col-span-7">
            <div className="eyebrow text-taupe mb-8">From My Desk</div>
            <h1 className="font-serif-display text-6xl md:text-7xl lg:text-[8rem] leading-[0.94] tracking-tightest text-matte">
              Founder <em className="italic text-taupe">Journal.</em>
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-matte/70 leading-relaxed text-[15px] max-w-md">
              Entries from the desk of Toia Wallace, on building companies, deploying AI, and the
              quiet decisions that compound into an ecosystem.
            </p>
          </div>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <PageHero
            src={PHOTOS.journalPage}
            alt="Toia Wallace, journal"
            mobileAnchor="top"
            desktopAnchor="top"
            desktopHeight="md:h-[70vh] lg:h-[80vh]"
            mobileAspect="aspect-[4/5]"
            testId="journal-hero"
          />
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1200px] px-6 sm:px-10 lg:px-16">
          <SectionMarker number="01" label="Entries" className="mb-16" />
          <div className="space-y-2">
            {entries === null && <p className="py-16 text-matte/50" data-testid="journal-loading">Loading entries…</p>}
            {entries && entries.length === 0 && <p className="py-16 text-matte/60" data-testid="journal-empty">New entries are being prepared.</p>}
            {(entries || []).map((e, i) => <JournalRow key={e.slug} entry={e} i={i} />)}
          </div>
        </div>
      </section>

      {downloads.length > 0 && (
        <section className="pb-24 lg:pb-32" data-testid="journal-resources">
          <div className="mx-auto max-w-[1200px] px-6 sm:px-10 lg:px-16">
            <SectionMarker number="02" label="Journal Resources" className="mb-16" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
              {downloads.map((p) => <ProductCard key={p.id} product={p} section="journal" />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function JournalRow({ entry, i }) {
  const ref = useReveal();
  return (
    <Link
      to={`/journal/${entry.slug}`}
      ref={ref}
      data-testid={J_TID.card(entry.slug)}
      style={{ transitionDelay: `${i * 60}ms` }}
      className="reveal group border-t border-taupe/25 py-14 lg:py-16 grid lg:grid-cols-12 gap-8 lg:gap-16 items-start"
    >
      <div className="lg:col-span-3">
        <div className="eyebrow text-taupe mb-2">{entry.kicker || 'From My Desk'}</div>
        <div className="font-label text-[11px] tracking-widest uppercase text-matte/50">
          {formatMonthYear(entry.published_date)}{entry.read_time ? ` · ${entry.read_time}` : ''}
        </div>
      </div>
      <div className="lg:col-span-8">
        <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tightest leading-[1.02] text-matte group-hover:text-gold transition-colors duration-500">
          {entry.title}
        </h2>
        <p className="mt-6 text-matte/70 leading-relaxed max-w-2xl">{entry.short_description}</p>
        {entry.excerpt && (
          <p className="mt-6 font-serif-display italic text-2xl text-taupe leading-snug max-w-3xl">
            {entry.excerpt}
          </p>
        )}
      </div>
      <div className="lg:col-span-1 lg:justify-self-end pt-2">
        <ArrowRight size={22} strokeWidth={1.2} className="text-matte/40 group-hover:text-gold group-hover:translate-x-1 transition-all duration-500" />
      </div>
    </Link>
  );
}
